import React, { useState, useEffect } from 'react';
import agentApiService from '../../services/agentApiService';
import './TokenRecharge.css';

const TokenRecharge = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agentDetails, setAgentDetails] = useState(null);
  const [stats, setStats] = useState(null);
  const [selectedTokens, setSelectedTokens] = useState('100');
  const [rechargeAmount, setRechargeAmount] = useState('200');
  const [showModal, setShowModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchTokenRechargeDetails();
    fetchTokenRechargeStats();
  }, []);

  const fetchTokenRechargeDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🪙 [TOKEN-RECHARGE] Fetching token recharge details...');
      const response = await agentApiService.getTokenRechargeDetails();
      
      if (response.success) {
        setAgentDetails(response.agentDetails);
        console.log('✅ [TOKEN-RECHARGE] Token recharge details loaded successfully');
      } else {
        throw new Error(response.message || 'Failed to fetch token recharge details');
      }
    } catch (err) {
      console.error('❌ [TOKEN-RECHARGE] Error fetching token recharge details:', err);
      setError(err.message || 'Failed to load token recharge details');
    } finally {
      setLoading(false);
    }
  };

  const fetchTokenRechargeStats = async () => {
    try {
      console.log('📊 [TOKEN-RECHARGE] Fetching token recharge statistics...');
      const response = await agentApiService.getTokenRechargeStats();
      
      if (response.success) {
        setStats(response.stats);
        console.log('✅ [TOKEN-RECHARGE] Token recharge statistics loaded successfully');
      } else {
        console.warn('⚠️ [TOKEN-RECHARGE] Failed to fetch statistics:', response.message);
      }
    } catch (err) {
      console.error('❌ [TOKEN-RECHARGE] Error fetching token recharge statistics:', err);
      // Don't set error for stats failure, just log it
    }
  };

  const handleTokenChange = (event) => {
    const tokens = event.target.value;
    setSelectedTokens(tokens);
    // Calculate price: 1 token = $2
    const price = parseInt(tokens) * 2;
    setRechargeAmount(price.toString());
  };

  const handlePayNow = () => {
    setShowModal(true);
  };

  const handleConfirmPayment = async () => {
    try {
      setProcessing(true);
      setError(null);
      
      console.log('💳 [TOKEN-RECHARGE] Processing token recharge...', {
        tokens: selectedTokens,
        rechargeAmount: rechargeAmount
      });

      const rechargeData = {
        tokens: selectedTokens,
        rechargeAmount: rechargeAmount
      };

      const response = await agentApiService.processTokenRecharge(rechargeData);
      
      if (response.success) {
        setSuccessMessage(`Successfully recharged ${selectedTokens} tokens for $${rechargeAmount}!`);
        setShowModal(false);
        
        // Refresh data
        await fetchTokenRechargeDetails();
        await fetchTokenRechargeStats();
        
        console.log('✅ [TOKEN-RECHARGE] Token recharge processed successfully');
      } else {
        throw new Error(response.message || 'Failed to process token recharge');
      }
    } catch (err) {
      console.error('❌ [TOKEN-RECHARGE] Error processing token recharge:', err);
      setError(err.message || 'Failed to process token recharge');
    } finally {
      setProcessing(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleCloseSuccess = () => {
    setSuccessMessage('');
  };

  if (loading) {
    return (
      <div className="token-recharge-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading token recharge details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="token-recharge-container">
        <div className="error-message">
          <h3>Error Loading Token Recharge</h3>
          <p>{error}</p>
          <button onClick={fetchTokenRechargeDetails} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="token-recharge-container">
      {/* Header */}
      <div className="page-header">
        <h1>Token Recharge</h1>
        <p>Recharge your account tokens to continue using our services</p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="success-alert">
          <div className="alert-content">
            <span className="success-icon">✅</span>
            <span>{successMessage}</span>
            <button onClick={handleCloseSuccess} className="close-alert">×</button>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">🪙</div>
            <div className="stat-content">
              <h3>Current Balance</h3>
              <p className="stat-value">{agentDetails?.tokens || 0} Tokens</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <h3>Total Recharges</h3>
              <p className="stat-value">{stats.totalRecharges}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <h3>Total Spent</h3>
              <p className="stat-value">${stats.totalAmountSpent?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🎯</div>
            <div className="stat-content">
              <h3>Avg. Recharge</h3>
              <p className="stat-value">${stats.averageRechargeAmount?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="recharge-content">
        {/* Recharge Image */}
        <div className="recharge-image">
          <div className="token-icon">🪙</div>
          <h2>Recharge Tokens</h2>
          <p>Select the number of tokens you want to purchase</p>
        </div>

        {/* Recharge Form */}
        <div className="recharge-form">
          <div className="form-group">
            <label htmlFor="tokens">Select No. of Tokens</label>
            <select 
              id="tokens" 
              value={selectedTokens} 
              onChange={handleTokenChange}
              className="form-select"
            >
              <option value="100">100 Tokens</option>
              <option value="200">200 Tokens</option>
              <option value="300">300 Tokens</option>
              <option value="400">400 Tokens</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="price">Price</label>
            <input 
              type="text" 
              id="price" 
              value={`$${rechargeAmount}`} 
              readOnly 
              className="form-input price-input"
            />
          </div>

          <button 
            onClick={handlePayNow} 
            className="pay-button"
            disabled={processing}
          >
            {processing ? 'Processing...' : 'Pay Now'}
          </button>

          {/* Payment Methods Info */}
          <div className="payment-info">
            <p>Secure payment processing</p>
            <div className="payment-icons">
              <span className="payment-icon">💳</span>
              <span className="payment-icon">🏦</span>
              <span className="payment-icon">📱</span>
            </div>
          </div>
        </div>
      </div>

      {/* Agent Details */}
      {agentDetails && (
        <div className="agent-info">
          <h3>Account Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Agent ID:</span>
              <span className="info-value">{agentDetails.agentId}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Name:</span>
              <span className="info-value">{agentDetails.name}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Current Tokens:</span>
              <span className="info-value">{agentDetails.tokens} Tokens</span>
            </div>
            <div className="info-item">
              <span className="info-label">Wallet Balance:</span>
              <span className="info-value">${agentDetails.walletBalance || '0.00'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Payment</h3>
              <button onClick={handleCloseModal} className="close-modal">×</button>
            </div>
            <div className="modal-body">
              <p>Do you want to proceed with the payment?</p>
              <div className="payment-summary">
                <div className="summary-item">
                  <span>Tokens:</span>
                  <span>{selectedTokens} Tokens</span>
                </div>
                <div className="summary-item">
                  <span>Amount:</span>
                  <span>${rechargeAmount}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                onClick={handleCloseModal} 
                className="cancel-button"
                disabled={processing}
              >
                No
              </button>
              <button 
                onClick={handleConfirmPayment} 
                className="confirm-button"
                disabled={processing}
              >
                {processing ? 'Processing...' : 'Yes, Pay Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenRecharge;